import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import AnimatedNumber from "../../common/components/AnimatedNumber";
import BoldNumberCard from "../../common/components/BoldNumberCard";
import CardChartMenu from "../../common/components/CardChartMenu";
import ChartRenderer from "../../common/components/ChartRenderer";
import { InfoPopover } from "../../common/components/InfoPopover";
import { CHART_TYPES } from "../../common/constants/cardRegistry";
import {
  CHART_CONFIGS,
  formatChartLabels,
} from "../../common/config/chartConfig";
import { useSettings } from "../../common/contexts/SettingsContext";
import { GraphColorPalette } from "./GraphColorPalette";

const ROIOverTime = ({
  analytics,
  chartType,
  allowedChartTypes,
  onChartTypeChange,
  comparisonDatasets,
  cardKey,
  configKey,
}) => {
  const { t } = useTranslation("analytics");
  const {
    setShowROIOverTime,
    setRoiOverTimeGraphColor,
    roiOverTimeGraphColor,
  } = useSettings();
  const [showGraphColorPalette, setShowGraphColorPalette] = useState(false);

  if (!analytics || !analytics.time_stats)
    return <Typography>{t("loading")}</Typography>;

  const granularity = analytics.granularity || "monthly";
  const timeStats = analytics.time_stats;
  const labels = formatChartLabels(timeStats, granularity);
  const roiData = timeStats.map((item) => item.roi || 0);
  const totalROI = roiData.reduce((sum, roi) => sum + (roi || 0), 0);
  const defaultROIColor =
    totalROI > 0 ? "#28a745" : totalROI < 0 ? "#dc3545" : "#6c757d";
  const isBold = chartType === CHART_TYPES.BOLD_NUMBER;

  return (
    <>
      <Grid size={{ xs: 12, md: isBold ? 4 : 6 }} sx={{ mb: 3 }}>
        <Card variant="outlined" sx={{ height: "100%" }}>
          <CardContent>
            <Box sx={{ width: "100%", maxWidth: "1200px", margin: "auto" }}>
              <Box
                sx={{
                  pb: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      textTransform: "uppercase",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      color: "text.secondary",
                    }}
                  >
                    {t("chartLabels.roi")}
                  </Typography>
                  <InfoPopover text={t("chartInfo.roi")} />
                </Box>
                <CardChartMenu
                  onHide={() => setShowROIOverTime(false)}
                  onColorChange={() => setShowGraphColorPalette(true)}
                  chartType={chartType}
                  allowedChartTypes={allowedChartTypes}
                  onChartTypeChange={onChartTypeChange}
                  cardKey={cardKey}
                  configKey={configKey}
                />
              </Box>

              {isBold ? (
                <BoldNumberCard
                  total={totalROI}
                  dataValues={roiData}
                  color={roiOverTimeGraphColor || defaultROIColor}
                  isCurrency
                />
              ) : (
                <>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "2.3rem",
                        color: "text.primary",
                      }}
                    >
                      <AnimatedNumber
                        section="chartTotals"
                        value={totalROI}
                        isCurrency
                      />
                    </Typography>
                  </Box>
                  <ChartRenderer
                    chartType={chartType}
                    labels={labels}
                    dataValues={roiData}
                    datasets={comparisonDatasets}
                    color={roiOverTimeGraphColor || defaultROIColor}
                    chartTitle="ROI"
                    chartOptions={CHART_CONFIGS.currency}
                  />
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <GraphColorPalette
        showGraphColorPalette={showGraphColorPalette}
        setShowGraphColorPalette={setShowGraphColorPalette}
        onSelectColor={setRoiOverTimeGraphColor}
      />
    </>
  );
};

export default ROIOverTime;
